
import React, { useEffect, useState } from 'react';
import { fetchTranscriptByShareId } from '../services/historyService';
import { TranscriptionHistoryItem } from '../types';
import { FileText, Clock, AlertCircle, MicVocal } from 'lucide-react';

interface PublicTranscriptPageProps {
  shortId: string;
}

const formatDuration = (ms: number) => {
  if (!ms || ms <= 0) return '';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  return `${seconds}s`;
};

const PublicTranscriptPage: React.FC<PublicTranscriptPageProps> = ({ shortId }) => {
  const [transcript, setTranscript] = useState<TranscriptionHistoryItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Set noindex meta tag to prevent search engine indexing
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTranscriptByShareId(shortId).then(data => {
      if (cancelled) return;
      if (!data) {
        setNotFound(true);
      } else {
        setTranscript(data as TranscriptionHistoryItem);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [shortId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 dark:bg-dark-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-base-content/50 dark:text-dark-content/50">
          <div className="w-10 h-10 border-4 border-brand-primary/30 border-t-brand-primary rounded-full animate-spin" />
          <p className="text-sm font-medium">Loading transcript…</p>
        </div>
      </div>
    );
  }

  if (notFound || !transcript) {
    return (
      <div className="min-h-screen bg-base-100 dark:bg-dark-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white dark:bg-dark-200 rounded-3xl p-10 border border-base-200 dark:border-dark-300 shadow-xl">
          <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500 mx-auto mb-6">
            <AlertCircle size={32} />
          </div>
          <h1 className="text-2xl font-bold text-base-content dark:text-dark-content mb-2">Transcript Not Found</h1>
          <p className="text-base-content/60 dark:text-dark-content/60 text-sm">
            This share link is invalid or has been revoked by the owner.
          </p>
        </div>
      </div>
    );
  }

  const segments = (() => {
    const lines = transcript.transcript.split('\n');
    const timeRegex = /\[(\d{1,2}:)?(\d{1,2}):(\d{2})\]/;
    return lines
      .map(line => {
        const match = line.match(timeRegex);
        if (!match) return null;
        const timestamp = match[0];
        const text = line.replace(timestamp, '').trim();
        return { timestamp: timestamp.replace(/[\[\]]/g, ''), text };
      })
      .filter(Boolean) as { timestamp: string; text: string }[];
  })();

  return (
    <div className="min-h-screen bg-base-100 dark:bg-dark-100">
      <header className="border-b border-base-200 dark:border-dark-300 bg-white/80 dark:bg-dark-200/80 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center">
              <MicVocal size={18} className="text-white" />
            </div>
            <span className="font-bold text-base-content dark:text-dark-content">EchoScribe</span>
          </div>
          <span className="text-xs px-3 py-1 rounded-full bg-base-200 dark:bg-dark-300 text-base-content/50 dark:text-dark-content/50 font-medium uppercase tracking-wide">
            Read-only
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-base-content dark:text-dark-content tracking-tight mb-2 truncate">
            {transcript.fileName}
          </h1>
          <div className="flex items-center gap-4 text-sm text-base-content/50 dark:text-dark-content/50">
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {new Date(transcript.timestamp).toLocaleDateString(undefined, { dateStyle: 'medium' })}
            </span>
            {transcript.duration > 0 && (
              <span className="flex items-center gap-1.5">
                <FileText size={14} />
                {formatDuration(transcript.duration)}
              </span>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-dark-200 rounded-3xl border border-base-200 dark:border-dark-300 shadow-sm overflow-hidden">
          <div className="p-6 md:p-10">
            {segments.length > 0 ? (
              <div className="space-y-4">
                {segments.map((seg, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <span className="shrink-0 mt-1 font-mono text-xs font-bold px-2 py-1 rounded-lg bg-base-200 dark:bg-dark-300 text-base-content/50 dark:text-dark-content/40">
                      {seg.timestamp}
                    </span>
                    <p className="text-base-content/80 dark:text-dark-content/80 leading-relaxed text-lg">
                      {seg.text}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="whitespace-pre-wrap leading-relaxed text-base-content/80 dark:text-dark-content/80 text-lg">
                {transcript.transcript}
              </p>
            )}
          </div>
        </div>

        <p className="mt-8 text-center text-xs text-base-content/30 dark:text-dark-content/30">
          Shared via EchoScribe — AI-powered transcription
        </p>
      </main>
    </div>
  );
};

export default PublicTranscriptPage;
